// searchRows — the pure read model behind the Search page (pen Q82 "Search
// and a minimal left menu", decided 3 Nov). Two fixed tabs, Sources (first,
// the default) and Materials; the field says what, the tab says where.
//
// A query (CQRS read side): it folds the loaded library + the field's text
// + the tab and sort into rows. Nothing here writes. No display strings:
// closed sums the view localizes.
//
// The field is one string (frame state, the URL's `?q=`), like GitHub's:
// free words plus qualifiers. `kind:quiz` (a material's kind, the Materials
// Filter), `type:pdf` (a source's file type, the Sources Filter) and
// `is:archived` ("Show archived") show as pills; every other word, an
// unknown or misspelt qualifier included, is plain title text (the default
// case). Filters belong to their tab: switching tabs keeps the words and
// drops the qualifiers.
//
// The field is hostile input: it is clipped to {query-max}
// (Core.Limits.SEARCH_QUERY_MAX), matched as plain text (no pattern
// language, no markup), and a list shows {results}
// (Core.Limits.SEARCH_RESULTS_MAX) rows a page. Cost: one pass over the
// pool per keystroke, O(rows x words x title length); the pool is the
// author's own library.

import * as Core from "@bilda/core";
import type { ProjectItem } from "../../app/ports/project.js";
import type { Library, LibraryItem } from "../../app/ports/workspace.js";
import {
    type FileKind,
    fileKindOf,
} from "../KnowledgeBank/knowledgeBankModel.js";
import type {
    SearchFacet,
    SearchResult,
    TextSegment,
} from "../Search/searchModel.js";

export type {
    FieldQuery,
    MaterialKind,
    MaterialRow,
    MaterialSortField,
    SearchTab,
    SortOrder,
    SourceRow,
    SourceSortField,
    TabSort,
};
export {
    archivedCount,
    clipQuery,
    effectiveSort,
    FILE_KINDS,
    facetOfTab,
    fieldQueryOf,
    MATERIAL_KINDS,
    matchSegments,
    materialRowsOf,
    printFieldQuery,
    queryWords,
    RESULTS_PAGE,
    SEARCH_QUERY_MAX,
    shownMaterials,
    shownSources,
    sourceRowsOf,
    tabOfFacet,
    textMatches,
    withoutFilters,
};

// === Tabs ===================================================================

type SearchTab = "sources" | "materials";

// The tab's facet (a `/library?filter=` link, a menu entry) → the tab.
// Materials and their packages open Materials; everything else, or none,
// opens Sources (the default tab).
const tabOfFacet = (facet: SearchFacet | undefined): SearchTab => {
    switch (facet) {
        case "material":
        case "package":
            return "materials";
        case "source":
        case "file":
        case "conversation":
        case undefined:
            return "sources";
    }
};

const facetOfTab = (tab: SearchTab): SearchFacet => {
    switch (tab) {
        case "materials":
            return "material";
        case "sources":
            return "source";
    }
};

// === The field ==============================================================

const SEARCH_QUERY_MAX = Core.Limits.SEARCH_QUERY_MAX;
// One page of a list: {results}.
const RESULTS_PAGE = Core.Limits.SEARCH_RESULTS_MAX;

// What a material is, in the words the page uses (a package is what an
// export made; any other shape reads as a document: the default).
type MaterialKind =
    | "lesson"
    | "quiz"
    | "document"
    | "module"
    | "course"
    | "package";

const MATERIAL_KINDS: readonly MaterialKind[] = [
    "lesson",
    "quiz",
    "document",
    "module",
    "course",
    "package",
];

const FILE_KINDS: readonly FileKind[] = [
    "pdf",
    "word",
    "presentation",
    "text",
    "image",
    "audio",
    "video",
    "unknown",
];

// The field, read: its free words (as typed) and the qualifiers it holds.
type FieldQuery = {
    readonly text: string;
    readonly kind?: MaterialKind;
    readonly type?: FileKind;
    readonly archived: boolean;
};

// The field as the page holds it: at most {query-max} characters (the
// input stops at the same number; a longer link is cut, never refused
// loudly).
const clipQuery = (raw: string): string =>
    [...raw].slice(0, SEARCH_QUERY_MAX).join("");

const qualifier = (word: string, key: string): string | undefined =>
    word.toLowerCase().startsWith(`${key}:`)
        ? word.slice(key.length + 1).toLowerCase()
        : undefined;

// Total: every string reads as a query. A qualifier with a value the page
// does not know is a plain word; the last of two same qualifiers wins. The
// words keep their spaces as typed (a trailing one is a word still being
// written), minus each qualifier with the space before it, and the space
// before the first word.
const fieldQueryOf = (raw: string): FieldQuery => {
    type Read = { readonly q: FieldQuery; readonly words: readonly string[] };
    const take = ({ words }: Read, next: FieldQuery): Read => ({
        q: next,
        words: /^\s+$/u.test(words.at(-1) ?? "") ? words.slice(0, -1) : words,
    });
    const read = clipQuery(raw)
        .split(/(\s+)/u)
        .reduce<Read>(
            (acc, part) => {
                const kind = MATERIAL_KINDS.find(
                    (k) => k === qualifier(part, "kind"),
                );
                if (kind !== undefined) return take(acc, { ...acc.q, kind });
                const type = FILE_KINDS.find(
                    (k) => k === qualifier(part, "type"),
                );
                if (type !== undefined) return take(acc, { ...acc.q, type });
                if (qualifier(part, "is") === "archived")
                    return take(acc, { ...acc.q, archived: true });
                return { q: acc.q, words: [...acc.words, part] };
            },
            { q: { text: "", archived: false }, words: [] },
        );
    return { ...read.q, text: read.words.join("").trimStart() };
};

// Back to the field's one string: the qualifiers first (they show as
// pills before the words), then the words.
const printFieldQuery = (q: FieldQuery): string =>
    [
        q.kind === undefined ? "" : `kind:${q.kind}`,
        q.type === undefined ? "" : `type:${q.type}`,
        q.archived ? "is:archived" : "",
        q.text,
    ]
        .filter((part) => part !== "")
        .join(" ");

// Switching tabs: the words stay, the filters (they belong to a tab) go.
const withoutFilters = (q: FieldQuery): FieldQuery => ({
    text: q.text,
    archived: false,
});

// Case-, width- and accent-insensitive comparison form, keeping å/ä/ö
// distinct like the server's content search (Swedish letters). One
// character in, one out (a decomposed accent is dropped with its base
// kept), so a match's place in the folded text is its place in the title;
// `matchSegments` checks that and marks nothing when it does not hold.
const KEEP = /[åäöÅÄÖ]/u;
const foldChar = (ch: string): string =>
    (KEEP.test(ch)
        ? ch
        : ch.normalize("NFD").replace(/\p{Mn}/gu, "")
    ).toLocaleLowerCase();
const fold = (text: string): string =>
    [...text.normalize("NFC")].map(foldChar).join("");

// The free words of a text, folded; quotes are just characters.
const queryWords = (text: string): readonly string[] =>
    fold(clipQuery(text))
        .split(/\s+/u)
        .filter((w) => w !== "");

// Plain-text match: every word occurs somewhere in one of the texts.
const textMatches = (
    words: readonly string[],
    texts: readonly string[],
): boolean => {
    if (words.length === 0) return true;
    const hay = fold(texts.join("\u0000"));
    return words.every((w) => hay.includes(w));
};

// A title cut into plain and matched runs (the row marks the matched ones
// on a neutral ground). No words, or a title whose folded form is not one
// character per character: one plain run.
const matchSegments = (
    title: string,
    words: readonly string[],
): readonly TextSegment[] => {
    const chars = [...title.normalize("NFC")];
    const folded = chars.map(foldChar);
    if (words.length === 0 || folded.some((c) => [...c].length !== 1))
        return [{ text: title, matched: false }];
    const hay = folded.join("");
    const marked = chars.map(() => false);
    for (const word of words) {
        const len = [...word].length;
        let at = hay.indexOf(word);
        while (at !== -1 && len > 0) {
            for (let i = at; i < at + len; i++) marked[i] = true;
            at = hay.indexOf(word, at + len);
        }
    }
    return chars.reduce<TextSegment[]>((runs, ch, i) => {
        const matched = marked[i] === true;
        const last = runs.at(-1);
        if (last !== undefined && last.matched === matched)
            return [...runs.slice(0, -1), { text: last.text + ch, matched }];
        return [...runs, { text: ch, matched }];
    }, []);
};

// How well a title answers the words (Best match): a word at the title's
// start counts most, then at a word's start, then anywhere in it.
const matchScore = (title: string, words: readonly string[]): number => {
    const t = fold(title);
    return words.reduce((score, w) => {
        if (t.startsWith(w)) return score + 3;
        if (t.includes(` ${w}`)) return score + 2;
        return t.includes(w) ? score + 1 : score;
    }, 0);
};

// === Sort ===================================================================

type SortOrder = "ascending" | "descending";

type MaterialSortField = "best" | "changed" | "name" | "kind" | "project";
type SourceSortField = "best" | "added" | "name" | "type" | "used-in";

type TabSort<F> = { readonly field: F; readonly order: SortOrder };

// With nothing typed, the newest first; while typing, the best match.
const defaultSort = <F extends string>(
    dated: F,
    typing: boolean,
): TabSort<F | "best"> =>
    typing
        ? { field: "best", order: "descending" }
        : { field: dated, order: "descending" };

// The sort in use: the author's choice, or the default (also when the
// choice was Best match and the field holds no words any more).
const effectiveSort = <F extends string>(
    chosen: TabSort<F> | null,
    dated: F,
    typing: boolean,
): TabSort<F | "best"> =>
    chosen === null || (chosen.field === "best" && !typing)
        ? defaultSort(dated, typing)
        : chosen;

const compareText = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

const ordered = (order: SortOrder, n: number): number =>
    order === "ascending" ? n : -n;

// === Shared =================================================================

// The project each resource lies in: on the project's canvas, or a part
// (at any depth) of something that is.
const projectsByResource = (
    library: Library,
): ReadonlyMap<string, ProjectItem> => {
    const partsOf = new Map(
        library.materials.map((m) => [m.id, m.parts ?? []]),
    );
    const out = new Map<string, ProjectItem>();
    const walk = (id: string, project: ProjectItem, seen: Set<string>) => {
        if (seen.has(id)) return;
        seen.add(id);
        if (!out.has(id)) out.set(id, project);
        for (const part of partsOf.get(id) ?? []) walk(part, project, seen);
    };
    for (const project of (library.projects ?? []).filter(
        (p) => p.lifecycle === "active",
    ))
        for (const item of project.canvas)
            walk(item.target, project, new Set());
    return out;
};

const itemsById = (library: Library): ReadonlyMap<string, LibraryItem> =>
    new Map(
        [
            ...library.sources,
            ...library.materials,
            ...library.exports,
            ...library.files,
            ...library.archived,
        ].map((i) => [i.id, i]),
    );

// Archived rows only when the field asks for them (`is:archived`, "Show
// archived"); then alongside the others.
const inArchive = (q: FieldQuery, result: SearchResult): boolean =>
    q.archived || result.archived !== true;

// === Materials ==============================================================

const materialKindOf = (
    result: SearchResult,
    item: LibraryItem | undefined,
): MaterialKind => {
    if (result.kind === "package") return "package";
    if (item?.type !== "material") return "document";
    if (item.container !== undefined) return item.container.kind;
    switch (item.document?.kind) {
        case "course":
        case "module":
        case "quiz":
            return item.document.kind;
        case "page":
        case "chapter":
            return "lesson";
        case "handout":
            return "document";
        case undefined:
            return item.template === "quiz" || item.template === "question"
                ? "quiz"
                : "document";
    }
};

type MaterialRow = {
    readonly result: SearchResult;
    readonly kind: MaterialKind;
    // The project it lives in (none: not on any active project).
    readonly project?: ProjectItem;
};

const materialRowsOf = (
    pool: readonly SearchResult[],
    library: Library | null,
): readonly MaterialRow[] => {
    if (library === null) return [];
    const items = itemsById(library);
    const projects = projectsByResource(library);
    return pool
        .filter((r) => r.kind === "material" || r.kind === "package")
        .map((result) => {
            const project = projects.get(result.id);
            return {
                result,
                kind: materialKindOf(result, items.get(result.id)),
                ...(project === undefined ? {} : { project }),
            };
        });
};

const compareMaterials =
    (sort: TabSort<MaterialSortField>, words: readonly string[]) =>
    (a: MaterialRow, b: MaterialRow): number => {
        const byName = compareText(a.result.title, b.result.title);
        const byChanged = (a.result.editedAt ?? "").localeCompare(
            b.result.editedAt ?? "",
        );
        switch (sort.field) {
            case "best":
                return (
                    matchScore(b.result.title, words) -
                        matchScore(a.result.title, words) ||
                    -byChanged ||
                    byName
                );
            case "name":
                return ordered(sort.order, byName);
            case "kind":
                return (
                    ordered(sort.order, compareText(a.kind, b.kind)) || byName
                );
            case "project":
                return (
                    ordered(
                        sort.order,
                        compareText(
                            a.project?.title ?? "",
                            b.project?.title ?? "",
                        ),
                    ) || byName
                );
            case "changed":
                return ordered(sort.order, byChanged) || byName;
        }
    };

// The rows the Materials tab shows for the field: a source's type matches
// no material (its count reads 0, as the field says).
const shownMaterials = (
    rows: readonly MaterialRow[],
    q: FieldQuery,
    sort: TabSort<MaterialSortField>,
): readonly MaterialRow[] => {
    if (q.type !== undefined) return [];
    const words = queryWords(q.text);
    return rows
        .filter(
            (row) =>
                inArchive(q, row.result) &&
                (q.kind === undefined || q.kind === row.kind) &&
                textMatches(words, [row.result.title]),
        )
        .slice()
        .sort(compareMaterials(sort, words));
};

// === Sources ================================================================

type SourceUse = { readonly id: string; readonly title: string };

type SourceStateKey = "read" | "reading" | "unreadable";

type SourceRow = {
    readonly result: SearchResult;
    readonly fileKind: FileKind;
    // The active projects that use it.
    readonly usedIn: readonly SourceUse[];
    readonly state: SourceStateKey;
};

const sourceStateOf = (item: LibraryItem | undefined): SourceStateKey => {
    if (item?.type !== "source") return "read";
    switch (item.readStatus?.type) {
        case "source_read_failed":
            return "unreadable";
        case "source_reading_scanned_pages":
            return "reading";
        case "source_read_ready":
        case undefined:
            return "read";
    }
};

const sourceRowsOf = (
    pool: readonly SearchResult[],
    library: Library | null,
): readonly SourceRow[] => {
    if (library === null) return [];
    const items = itemsById(library);
    const projects = (library.projects ?? []).filter(
        (p) => p.lifecycle === "active",
    );
    return pool
        .filter((r) => r.kind === "source" || r.kind === "file")
        .map((result) => {
            const item = items.get(result.id);
            return {
                result,
                fileKind: fileKindOf(item?.title ?? result.title),
                usedIn: projects
                    .filter((p) => p.canvas.some((c) => c.target === result.id))
                    .map((p) => ({ id: p.id, title: p.title })),
                state: sourceStateOf(item),
            };
        });
};

const compareSources =
    (sort: TabSort<SourceSortField>, words: readonly string[]) =>
    (a: SourceRow, b: SourceRow): number => {
        const byName = compareText(a.result.title, b.result.title);
        const byAdded = (a.result.createdAt ?? "").localeCompare(
            b.result.createdAt ?? "",
        );
        switch (sort.field) {
            case "best":
                return (
                    matchScore(b.result.title, words) -
                        matchScore(a.result.title, words) ||
                    -byAdded ||
                    byName
                );
            case "name":
                return ordered(sort.order, byName);
            case "type":
                return (
                    ordered(sort.order, compareText(a.fileKind, b.fileKind)) ||
                    byName
                );
            case "used-in":
                return (
                    ordered(sort.order, a.usedIn.length - b.usedIn.length) ||
                    byName
                );
            case "added":
                return ordered(sort.order, byAdded) || byName;
        }
    };

// The rows the Sources tab shows for the field: a material's kind matches
// no source (its count reads 0, as the field says).
const shownSources = (
    rows: readonly SourceRow[],
    q: FieldQuery,
    sort: TabSort<SourceSortField>,
): readonly SourceRow[] => {
    if (q.kind !== undefined) return [];
    const words = queryWords(q.text);
    return rows
        .filter(
            (row) =>
                inArchive(q, row.result) &&
                (q.type === undefined || q.type === row.fileKind) &&
                textMatches(words, [
                    row.result.title,
                    ...(row.result.authors ?? []),
                ]),
        )
        .slice()
        .sort(compareSources(sort, words));
};

// "Show archived (N)": how many archived rows the field would bring in
// (0 once it shows them).
const archivedCount = <R extends { readonly result: SearchResult }>(
    rows: readonly R[],
    q: FieldQuery,
    shown: (rows: readonly R[], q: FieldQuery) => readonly R[],
): number =>
    q.archived
        ? 0
        : shown(rows, { ...q, archived: true }).filter(
              (row) => row.result.archived === true,
          ).length;
